/**
 * Sino de notificações do header — C-24. TS puro.
 *
 * Fiel ao `buildNotifications` do protótipo V8 (L1183), com a decisão do dono de
 * 17/10/2026: o sino lista as críticas E as secundárias (o V8 listava só as secundárias e
 * deixava as críticas para a home).
 *
 * O sino é a lista do que AINDA PEDE AÇÃO: cada item fica enquanto a pendência existe e
 * sai sozinho quando ela é resolvida. É o oposto do Log (M10), que é o histórico do que já
 * aconteceu. "Lido" só apaga o número do badge — nunca tira o item da lista.
 *
 * Fontes:
 *  - instrumentos: `itensQuePedemAcao`, a MESMA lista do "Precisa da sua atenção" (nível,
 *    sem sinal pela regra da tela, supressão por TARP em curso);
 *  - TARPs não baixadas: texto por perfil de `buildTarpNotificacao` (GUPM-177) — a TARP
 *    aberta é o que representa o instrumento que a supressão tirou da lista;
 *  - planos de ação com ação não concluída;
 *  - leitura atrasada (`computeAtrasos`): o degrau ANTES do "sem sinal", só aqui no sino,
 *    como secundária e sem botão de TARP (decisão do dono de 20/10/2026).
 *
 * Fora, por não haver fonte: câmeras sem sinal (sem API — M5), diagnósticos técnicos,
 * calibração de NA e coordenador do PAEBM (pendências fixas no protótipo).
 */
import type { ItemAtencao } from '../dashboard/atencao';
import type { TarpParaBanner } from '../dashboard/anomalias';
import {
  descricaoDoItemDeAtencao, rotuloDaAcaoDeAtencao, tituloDoItemDeAtencao,
} from '../dashboard/textos-atencao';
import { rotuloDaEstrutura } from '../estrutura/rotulo';
import { diaEmBrasilia, FUSO_BRASILIA } from '../fuso-brasilia';
import { verInstrumentoNavState } from '../instrumentos/navegacao';
import { tempoSemSinal } from '../instrumentos/perda-de-sinal';
import type { Plano } from '../planos';
import { acaoVencida } from '../planos/mpa';
import { openPlanoNavState } from '../planos/navegacao';
import {
  atalhoSemFluxoNavState,
  openTarpNavState,
  resolverAtalhoDeFluxo,
  resolverAtalhoDeSemSinal,
  type ResultadoAtalho,
} from '../tarp/disparo';
import { nivelEfetivoVisivel } from '../tarp/nivel-efetivo';
import {
  buildTarpNotificacao,
  severidadeFromNivel,
  type NotifProfile,
  type NotifRelevancia,
  type NotifSeveridade,
} from '../tarp/notificacoes';
import { nomeDoTipoDeInstrumento } from '../tarp/catalog';
import { formatarNumeroTarp } from '../tarp/numero';
import { calcPrazoBaixa, type PrazoBaixa } from '../tarp/prazo';
import type { Instrument, InstrumentType, TarpLevel } from '../types';
import type { InstrumentoAtrasado, ItemDeAtraso } from './atraso-de-leitura';

/** O que o clique no item faz. A navegação é resolvida por `navegacaoDaAcao`. */
export type AcaoDoSino =
  | { tipo: 'fluxo-nivel'; instrumentId: string }
  | { tipo: 'guia-sem-sinal'; instrumentId: string }
  | { tipo: 'abrir-tarp'; tarpId: string }
  | { tipo: 'abrir-plano'; planoId: string }
  | { tipo: 'ver-grafico'; instrumentId: string }
  | { tipo: 'ver-mapa' };

export interface NotificacaoDoSino {
  /** Identidade do item na tela (estável enquanto a pendência existir). */
  id: string;
  /**
   * Identidade do ESTADO lido. Muda quando a pendência piora (nível sobe, novo episódio de
   * sem sinal, TARP muda de status, nova ação vence): o item volta a contar como não lido.
   * Escalada nunca fica escondida atrás de um "lido" antigo.
   */
  chaveLeitura: string;
  sev: NotifSeveridade;
  relev: NotifRelevancia;
  titulo: string;
  descricao: string;
  ts: Date;
  rotuloAcao: string;
  acao: AcaoDoSino;
}

/** TARP como o sino a lê: os campos da supressão + número e escopo para o texto. */
export interface TarpParaSino extends TarpParaBanner {
  id: string;
  seq: number;
  damCode: string;
  instrumentCode: string | null;
}

export interface EntradaDoSino {
  itens: readonly ItemAtencao[];
  tarps: readonly TarpParaSino[];
  planos: readonly Plano[];
  /** Leituras atrasadas (`computeAtrasos`) — o degrau antes do "sem sinal". */
  atrasos: readonly ItemDeAtraso[];
  profile: NotifProfile;
  agora: Date;
}

const SEV_RANK: Record<NotifSeveridade, number> = { EMERGENCIA: 4, ALERTA: 3, ATENCAO: 2, INFO: 1 };

/** TARPs que ainda pedem ação de alguém. Rascunho não é TARP ainda; baixada já acabou. */
const STATUS_PENDENTES: ReadonlySet<string> = new Set(['ABERTA', 'CONCLUIDA_AGUARDANDO', 'EM_ANALISE_RT']);

function doItemDeAtencao(item: ItemAtencao, isVigilante: boolean, agora: Date): NotificacaoDoSino {
  const titulo = tituloDoItemDeAtencao(item, isVigilante, agora);
  const descricao = descricaoDoItemDeAtencao(item, isVigilante);
  if (item.kind === 'sem-sinal') {
    const id = `sem-sinal-${item.instrument.id}`;
    return {
      id,
      chaveLeitura: `${id}-${item.silencioDesde.getTime()}`,
      // ALERTA é o nível do `TARP-PS-TILT` no catálogo — o mesmo com que o Painel ordena o
      // card. O V8 usava EMERGÊNCIA (tiltímetro) e ATENÇÃO (demais); o Painel decidiu antes.
      sev: 'ALERTA',
      relev: 'critica',
      titulo,
      descricao,
      ts: item.ts,
      rotuloAcao: rotuloDaAcaoDeAtencao(item),
      acao: { tipo: 'guia-sem-sinal', instrumentId: item.instrument.id },
    };
  }
  const id = `nivel-${item.instrument.id}`;
  return {
    id,
    chaveLeitura: `${id}-${item.level}`,
    sev: severidadeFromNivel(item.level),
    relev: 'critica',
    titulo,
    descricao,
    ts: item.ts,
    rotuloAcao: rotuloDaAcaoDeAtencao(item),
    acao: { tipo: 'fluxo-nivel', instrumentId: item.instrument.id },
  };
}

function horas(h: number): string {
  return h < 1 ? 'menos de 1 h' : `${Math.floor(h)} h`;
}

/** "Prazo da baixa: vence em 14 h." — só para quem dá a baixa (RT/admin). */
function textoDoPrazo(prazo: PrazoBaixa): string {
  return prazo.estourado
    ? `Prazo da baixa: vencido há ${horas(-prazo.restanteH)}.`
    : `Prazo da baixa: vence em ${horas(prazo.restanteH)}.`;
}

function rotuloDaTarp(status: string, isRt: boolean): string {
  if (status === 'ABERTA') return isRt ? 'Ver' : 'Continuar';
  return isRt ? 'Avaliar' : 'Ver';
}

function daTarp(t: TarpParaSino, profile: NotifProfile, agora: Date): NotificacaoDoSino {
  const isRt = profile !== 'operador';
  const nivel = nivelEfetivoVisivel(t);
  const n = buildTarpNotificacao(
    {
      tarpCode: formatarNumeroTarp(t.seq),
      status: t.status,
      nivel,
      damCode: t.damCode,
      instrumentCode: t.instrumentCode,
    },
    profile,
  );
  // Prazo só para quem dá a baixa. `null` também quando a TARP ainda não foi encaminhada.
  const prazo = isRt && t.status !== 'ABERTA'
    ? calcPrazoBaixa(t.concludedAt, nivel, agora.toISOString())
    : null;
  const id = `tarp-${t.id}`;
  return {
    id,
    // O prazo que vence é pendência NOVA para o RT: a chave muda e o item volta a contar.
    chaveLeitura: `${id}-${t.status}-${n.sev}${prazo?.estourado ? '-prazo-vencido' : ''}`,
    sev: n.sev,
    relev: n.relev,
    titulo: n.titulo,
    descricao: prazo ? `${n.descricao} ${textoDoPrazo(prazo)}` : n.descricao,
    ts: new Date(t.status === 'ABERTA' ? t.openedAt : (t.concludedAt ?? t.openedAt)),
    rotuloAcao: rotuloDaTarp(t.status, isRt),
    acao: { tipo: 'abrir-tarp', tarpId: t.id },
  };
}

function doPlano(p: Plano, agora: Date): NotificacaoDoSino | null {
  if (p.status === 'concluido' || p.status === 'extinto') return null;
  const abertas = p.acoes.filter((a) => a.status !== 'concluido');
  if (abertas.length === 0) return null;
  const vencidas = abertas.filter((a) => acaoVencida(a, agora)).map((a) => a.id).sort();
  const id = `plano-${p.id}`;
  const partes = [p.titulo, rotuloDaEstrutura(p.damCode) ?? 'Estrutura não identificada'];
  if (vencidas.length > 0) partes.push(`${vencidas.length} com prazo vencido`);
  return {
    id,
    // QUAIS ações estão vencidas, não quantas: uma concluída e outra vencendo mantém a
    // contagem e é pendência nova.
    chaveLeitura: `${id}-v${vencidas.join(',')}`,
    sev: vencidas.length > 0 ? 'ATENCAO' : 'INFO',
    relev: 'secundaria',
    titulo: `Plano ${p.codigo}: ${abertas.length} ${abertas.length === 1 ? 'ação' : 'ações'} em aberto`,
    descricao: partes.join(' · '),
    ts: new Date(p.updatedAt ?? p.createdAt),
    rotuloAcao: 'Ver plano',
    acao: { tipo: 'abrir-plano', planoId: p.id },
  };
}

/** "30 min", "6h", "1h 30min" — a cadência esperada do tipo. */
function textoDaCadencia(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const resto = min % 60;
  return resto === 0 ? `${h}h` : `${h}h ${resto}min`;
}

const HORA_BRASILIA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_BRASILIA, hour: '2-digit', minute: '2-digit', hour12: false,
});
const DIA_MES_BRASILIA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_BRASILIA, day: '2-digit', month: '2-digit',
});

/**
 * "11:55", ou "18:45 de 18/10" quando o prazo não é de hoje. No fuso da barragem
 * (GUPM-332), não no da máquina: a sala de controle pode rodar em UTC.
 */
function horaDaChegada(prazo: Date, agora: Date): string {
  const hora = HORA_BRASILIA.format(prazo);
  return diaEmBrasilia(prazo) === diaEmBrasilia(agora) ? hora : `${hora} de ${DIA_MES_BRASILIA.format(prazo)}`;
}

/**
 * "última leitura há 6h 50min · esperado a cada 6h · deveria ter chegado até 11:55".
 * Sem a cadência no item de feed: um grupo de tipos misturados não tem UMA cadência.
 */
function detalheDoAtraso(a: InstrumentoAtrasado, agora: Date, comCadencia = true): string {
  return [
    `última leitura há ${tempoSemSinal(a.ultimaLeitura, agora)}`,
    ...(comCadencia ? [`esperado a cada ${textoDaCadencia(a.cadenciaMin)}`] : []),
    `deveria ter chegado até ${horaDaChegada(a.prazo, agora)}`,
  ].join(' · ');
}

/** "4 INA, 1 PZ" — mais numeroso primeiro; empate pela sigla. */
function contagemPorTipo(atrasados: readonly InstrumentoAtrasado[]): string {
  const porTipo = new Map<InstrumentType, number>();
  for (const a of atrasados) porTipo.set(a.instrument.type, (porTipo.get(a.instrument.type) ?? 0) + 1);
  return [...porTipo]
    .sort(([ta, na], [tb, nb]) => nb - na || ta.localeCompare(tb))
    .map(([tipo, n]) => `${n} ${tipo}`)
    .join(', ');
}

/**
 * Tipos do atraso que o M6 desenha (`MONITORED_TYPES` da feature, menos MS/MSE, que não
 * entram no atraso). O TILT está no M6 desde o GUPM-270. RL/MV/PLV são só overlay lá: vão
 * para o mapa. Cópia porque o domínio não importa da feature — a guarda
 * `grafico-do-sino-bate-com-o-m6` reprova se as duas listas se separarem.
 */
export const TIPOS_COM_GRAFICO_NO_M6: ReadonlySet<InstrumentType> = new Set(['INA', 'PZ', 'TILT']);

function daAtraso(item: ItemDeAtraso, isVigilante: boolean, agora: Date): NotificacaoDoSino {
  const base = { sev: 'ATENCAO', relev: 'secundaria' } as const;
  const estrutura = rotuloDaEstrutura(item.damCode) ?? 'Estrutura não identificada';
  if (item.kind === 'feed') {
    const primeiro = item.atrasados[0]!;
    const id = `atraso-feed-${item.damId}`;
    return {
      ...base,
      id,
      // A chave é a estrutura: dentro do mesmo apagão, sensor que entra ou que volta não é
      // pendência nova. Quando o apagão acaba o item sai, `podarLidas` esquece o lido e o
      // próximo apagão conta de novo.
      chaveLeitura: id,
      titulo: `Feed GPX: ${item.atrasados.length} de ${item.total} instrumentos da ${estrutura} com leitura atrasada`,
      descricao: [
        'Provável falha no envio da GPX, não nos sensores',
        // Quais tipos estão dentro: um tiltímetro no meio do apagão não some num número.
        contagemPorTipo(item.atrasados),
        detalheDoAtraso(primeiro, agora, false),
      ].join(' · '),
      ts: primeiro.prazo,
      rotuloAcao: 'Ver no mapa',
      acao: { tipo: 'ver-mapa' },
    };
  }
  const { instrument } = item;
  const id = `atraso-${instrument.id}`;
  const comGrafico = TIPOS_COM_GRAFICO_NO_M6.has(instrument.type);
  // Mesma convenção do "sem sinal": o vigilante lê o tipo no título e o código na descrição.
  const tipo = nomeDoTipoDeInstrumento(instrument.type) ?? 'Instrumento';
  return {
    ...base,
    id,
    // Leitura nova que atrasa de novo é outro episódio: volta a contar no badge.
    chaveLeitura: `${id}-${item.ultimaLeitura.getTime()}`,
    titulo: `${isVigilante ? tipo : instrument.code} com leitura atrasada`,
    descricao: `${isVigilante ? `Sensor ${instrument.code} · ` : ''}${estrutura} · ${detalheDoAtraso(item, agora)}`,
    // Desde quando está atrasado — o "há X" do rodapé conta a partir do prazo vencido.
    ts: item.prazo,
    rotuloAcao: comGrafico ? 'Ver gráfico' : 'Ver no mapa',
    acao: comGrafico ? { tipo: 'ver-grafico', instrumentId: instrument.id } : { tipo: 'ver-mapa' },
  };
}

/**
 * Monta a lista do sino: críticas primeiro; dentro de cada grupo, Emergência > Alerta >
 * Atenção > Info e, empatando, a mais recente primeiro.
 */
export function montarNotificacoesDoSino(e: EntradaDoSino): NotificacaoDoSino[] {
  const isVigilante = e.profile === 'operador';
  const lista: NotificacaoDoSino[] = [
    ...e.itens.map((i) => doItemDeAtencao(i, isVigilante, e.agora)),
    ...e.tarps.filter((t) => STATUS_PENDENTES.has(t.status)).map((t) => daTarp(t, e.profile, e.agora)),
    ...e.planos.map((p) => doPlano(p, e.agora)).filter((n): n is NotificacaoDoSino => n !== null),
    ...e.atrasos.map((a) => daAtraso(a, isVigilante, e.agora)),
  ];
  return lista.sort((a, b) => {
    if (a.relev !== b.relev) return a.relev === 'critica' ? -1 : 1;
    const d = SEV_RANK[b.sev] - SEV_RANK[a.sev];
    return d !== 0 ? d : b.ts.getTime() - a.ts.getTime();
  });
}

/** Quantas notificações ainda não foram lidas no estado atual. */
export function contarNaoLidas(
  notificacoes: readonly Pick<NotificacaoDoSino, 'chaveLeitura'>[],
  lidas: ReadonlySet<string>,
): number {
  return notificacoes.filter((n) => !lidas.has(n.chaveLeitura)).length;
}

/**
 * Esquece o "lido" de tudo que saiu do sino. Sem isto, uma pendência que normaliza e
 * volta horas depois — mesmo instrumento, mesmo nível, mesma chave — reapareceria como
 * lida, sem número no badge (achado da revisão do C-24). Se uma falha de carga esvaziar a
 * lista, os itens voltam a contar como não lidos: é o lado seguro.
 */
export function podarLidas(
  lidas: ReadonlySet<string>,
  chavesAtuais: readonly string[],
): ReadonlySet<string> {
  const atuais = new Set(chavesAtuais);
  const podadas = [...lidas].filter((k) => atuais.has(k));
  return podadas.length === lidas.size ? lidas : new Set(podadas);
}

/** Texto do badge: nada sem não lidas; "9+" acima de 9 (V8). */
export function rotuloDoBadge(naoLidas: number): string | null {
  if (naoLidas <= 0) return null;
  return naoLidas > 9 ? '9+' : String(naoLidas);
}

/** "agora", "há 12 min", "há 3h" ou a data — o `_notifTime` do V8. */
export function tempoRelativo(ts: Date, agora: Date): string {
  const min = Math.round((agora.getTime() - ts.getTime()) / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h}h`;
  return ts.toLocaleDateString('pt-BR');
}

export interface NavegacaoDoSino {
  path: '/tarp' | '/planos' | '/instrumentacao' | '/mapa';
  state: unknown;
}

function navDoAtalho(r: ResultadoAtalho): NavegacaoDoSino {
  return r.ok
    ? { path: '/tarp', state: { tarpAutoOpen: r.ctx } }
    : { path: '/tarp', state: atalhoSemFluxoNavState(r.motivo, r.instrumentCode) };
}

/**
 * Para onde o clique leva. Instrumentos resolvem pelo **id** (`resolverAtalhoDeFluxo`,
 * C-88): 18 códigos se repetem entre as barragens. Sem fluxo possível, a tela de TARP
 * recebe o motivo e avisa, em vez de abrir a lista em silêncio.
 */
export function navegacaoDaAcao(
  acao: AcaoDoSino,
  instruments: readonly Instrument[],
  itens: readonly ItemAtencao[],
): NavegacaoDoSino {
  switch (acao.tipo) {
    case 'fluxo-nivel': {
      const nivelDe = (i: { id: string }): TarpLevel | null => {
        for (const b of itens) if (b.kind === 'nivel' && b.instrument.id === i.id) return b.level;
        return null;
      };
      return navDoAtalho(resolverAtalhoDeFluxo(acao.instrumentId, instruments, nivelDe));
    }
    case 'guia-sem-sinal':
      return navDoAtalho(resolverAtalhoDeSemSinal(acao.instrumentId, instruments));
    case 'abrir-tarp':
      return { path: '/tarp', state: openTarpNavState(acao.tarpId) };
    case 'abrir-plano':
      return { path: '/planos', state: openPlanoNavState(acao.planoId) };
    case 'ver-grafico':
      return { path: '/instrumentacao', state: verInstrumentoNavState(acao.instrumentId) };
    case 'ver-mapa':
      return { path: '/mapa', state: null };
  }
}
